import React, { useState } from 'react';
import HeroSection from '../components/HeroSection';
import SEO from '../components/SEO';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import axios from 'axios';

const HERO_IMG = '/images/agm_2026.jpg';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MembershipPage() {
  const [form, setForm] = useState({
    name: '',
    contact_number: '',
    email: '',
    location: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [notRobot, setNotRobot] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      setForm({ name: '', contact_number: '', email: '', location: '', message: '' });
      setNotRobot(false);
    } catch (err) {
      setError('Something went wrong. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="membership-page">
      <SEO
        title="Membership — Join Round Table"
        description="Join Glossop & District Round Table 916. Open to men aged 18-45 for lifelong friendships, unforgettable experiences and community fundraising in Glossop, Derbyshire. Try it for free!"
        path="/membership"
        keywords={['join Round Table', 'Round Table membership', 'join Glossop Round Table', 'men 18-45 Glossop', 'community group Glossop', 'volunteer Glossop']}
      />
      {/* Hero */}
      <HeroSection
        image={HERO_IMG}
        title="Membership"
        subtitle="Join 30,000 men around the world for lifelong friendships"
        fullHeight={false}
      />

      {/* Info Section */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#F0A500] mb-4 block">
              Thinking of Joining?
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A] mb-6">
              Why not try it out for free!
            </h2>
            <div className="space-y-4 text-base text-[#52525B] leading-relaxed">
              <p>
                Are you male or do you identify as male, between the age of 18 and 45? You could join 30,000 men around the world for lifelong friendships, unforgettable experiences and the chance to make a difference to those around you.
              </p>
              <p>
                Are you looking for more information or to become a member? Please email our membership officer, Tom Plant on{' '}
                <a
                  href="mailto:membership@glossoproundtable.org"
                  className="text-[#F0A500] hover:text-[#D99500] font-semibold transition-colors"
                >
                  membership@glossoproundtable.org
                </a>{' '}or complete the form below.
              </p>
            </div>

            {/* Armed Forces */}
            <div className="mt-10 p-6 bg-[#F4F4F5] border-l-4 border-[#F0A500]">
              <h3 className="text-lg font-bold text-[#0A0A0A] mb-2">Membership Discounts</h3>
              <p className="text-sm text-[#52525B]">
                Are you in the armed forces or a veteran? As part of our dedication to the Armed Forces Covenant, we offer current and ex-servicemen 1 year free membership.
              </p>
            </div>

            {/* Tabler Magazine */}
            <p className="mt-6 text-base text-[#52525B]">
              As a Round Table member you will also receive{' '}
              <a
                href="https://www.roundtable.co.uk/magazine"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#F0A500] hover:text-[#D99500] font-semibold transition-colors"
              >
                Tabler magazine
              </a>.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section data-testid="contact-form-section" className="py-20 sm:py-28 bg-[#F4F4F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#F0A500] mb-4 block">
                Get In Touch
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A] mb-4">
                Contact Us
              </h2>
              <p className="text-base text-[#52525B]">
                Interested in joining or have a question? Fill out the form below and we'll get back to you.
              </p>
            </div>

            {submitted ? (
              <div data-testid="contact-form-success" className="bg-white p-8 text-center border border-[#F0A500]">
                <div className="w-12 h-12 bg-[#F0A500]/10 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-[#F0A500]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-[#0A0A0A] mb-2">Message Sent!</h3>
                <p className="text-sm text-[#52525B] mb-6">
                  Thank you for your interest. We'll be in touch soon.
                </p>
                <button
                  data-testid="contact-form-reset-btn"
                  onClick={() => setSubmitted(false)}
                  className="btn-amber inline-block text-sm"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form" className="bg-white p-6 sm:p-10 space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-[#0A0A0A] font-semibold mb-2 block">Name</Label>
                    <Input
                      id="name"
                      name="name"
                      data-testid="contact-input-name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      required
                      className="rounded-none border-zinc-300 focus:border-[#F0A500]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_number" className="text-[#0A0A0A] font-semibold mb-2 block">Contact Number</Label>
                    <Input
                      id="contact_number"
                      name="contact_number"
                      data-testid="contact-input-phone"
                      value={form.contact_number}
                      onChange={handleChange}
                      placeholder="Your phone number"
                      required
                      className="rounded-none border-zinc-300 focus:border-[#F0A500]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="email" className="text-[#0A0A0A] font-semibold mb-2 block">Email Address</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      data-testid="contact-input-email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      required
                      className="rounded-none border-zinc-300 focus:border-[#F0A500]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="location" className="text-[#0A0A0A] font-semibold mb-2 block">Location</Label>
                    <Input
                      id="location"
                      name="location"
                      data-testid="contact-input-location"
                      value={form.location}
                      onChange={handleChange}
                      placeholder="Your town or area"
                      required
                      className="rounded-none border-zinc-300 focus:border-[#F0A500]"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="text-[#0A0A0A] font-semibold mb-2 block">Message</Label>
                  <Textarea
                    id="message"
                    name="message"
                    data-testid="contact-input-message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Tell us what you're interested in..."
                    rows={5}
                    required
                    className="rounded-none border-zinc-300 focus:border-[#F0A500]"
                  />
                </div>

                {error && (
                  <p data-testid="contact-form-error" className="text-sm text-red-600">{error}</p>
                )}

                <div className="flex items-center gap-3 p-4 bg-[#F4F4F5] border border-zinc-300">
                  <Checkbox
                    id="not-robot"
                    data-testid="contact-not-robot-checkbox"
                    checked={notRobot}
                    onCheckedChange={(checked) => setNotRobot(checked === true)}
                  />
                  <Label htmlFor="not-robot" className="text-sm font-medium text-[#0A0A0A] cursor-pointer select-none">
                    I'm not a Robot
                  </Label>
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={submitting || !notRobot}
                  className="btn-amber w-full text-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
